import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Search, Plus, Clock, ArrowLeft } from 'lucide-react';
import { FoodSearch } from './FoodSearch';
import { MealTypeSelector } from './MealTypeSelector';
import { NutritionOverview } from './NutritionOverview';
import { MealsList } from './MealsList';
import { MealTemplateList } from './MealTemplateList';
import { searchableFoods } from '../../data/foodDatabase';
import { useMealStore } from '../../store/mealStore';
import { FoodItem } from '../../types/nutrition';

interface FoodLoggerProps {
  onClose: () => void;
}

export function FoodLogger({ onClose }: FoodLoggerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMealType, setSelectedMealType] = useState('breakfast');
  const [selectedFood, setSelectedFood] = useState<FoodItem | null>(null);
  const { addMeal } = useMealStore();

  const filteredFoods = searchQuery
    ? searchableFoods.filter(food =>
        food.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const handleAddFood = (food: FoodItem) => {
    addMeal({
      type: selectedMealType,
      time: new Date().toISOString(),
      foods: [{ ...food, servings: 1 }]
    });
    setSearchQuery('');
  };

  if (selectedFood) {
    return (
      <div className="fixed inset-0 bg-background/95 backdrop-blur-xl z-[100] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border p-4">
          <div className="flex items-center gap-4">
            <button onClick={() => setSelectedFood(null)}>
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-semibold">{selectedFood.name}</h2>
          </div>
        </div>

        <div className="p-4 space-y-6">
          {/* Food Image */}
          <div className="aspect-video rounded-lg overflow-hidden bg-muted">
            <img
              src={selectedFood.image || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800&q=80'}
              alt={selectedFood.name}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Macro Rings */}
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-orange-500">{selectedFood.protein || 0}g</div>
              <div className="text-sm text-muted-foreground">Protein</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-cyan-500">{selectedFood.carbs || 0}g</div>
              <div className="text-sm text-muted-foreground">Carbs</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-500">{selectedFood.fat || 0}g</div>
              <div className="text-sm text-muted-foreground">Fat</div>
            </div>
          </div>

          {/* Serving Size */}
          <div className="space-y-2">
            <label className="text-sm text-muted-foreground">Serving Size</label>
            <select className="w-full bg-muted p-3 rounded-lg">
              <option>1 serving (100g)</option>
              <option>1/2 serving (50g)</option>
              <option>2 servings (200g)</option>
            </select>
          </div>

          {/* Add Button */}
          <button
            onClick={() => {
              handleAddFood(selectedFood);
              setSelectedFood(null);
            }}
            className="w-full theme-button py-3 rounded-lg"
          >
            Add to {selectedMealType}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-xl z-[100] overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-semibold">Food Logger</h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-2xl space-y-6">
        {/* Meal Type Selection */}
        <MealTypeSelector
          selectedType={selectedMealType}
          onSelect={setSelectedMealType}
        />

        {/* Nutrition Overview */}
        <NutritionOverview />

        {/* Templates */}
        <MealTemplateList mealType={selectedMealType} />

        {/* Food Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="theme-card p-6 space-y-4"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-medium">Add Food</h3>
            <div className="text-sm text-muted-foreground">
              {selectedMealType.charAt(0).toUpperCase() + selectedMealType.slice(1)}
            </div>
          </div>

          <FoodSearch
            value={searchQuery}
            onChange={setSearchQuery}
            onSelect={setSelectedFood}
            filteredFoods={filteredFoods}
          />
        </motion.div>

        {/* Today's Meals */}
        <MealsList />
      </div>
    </div>
  );
}